import { useTheme } from '@material-ui/core';
import { Backdrop, ITab } from '@room-match/shared-ui-components';
import AsyncPhotoEditorV2 from 'components/AsyncPhotoEditorV2/AsyncPhotoEditorV2';
import { MemoizedErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { useSetHeaderBack, useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import IMedia from 'shared/interfaces/IMedia';
import { IProfileGraphQL, IProfileMediaSetSelectPayload } from 'shared/interfaces/IProfile';
import { ISkillCategory } from 'shared/interfaces/ISkills';
import { IOnProgress } from 'shared/interfaces/utils/IAxios';
import { mediaService } from 'shared/services/mediaService';
import { profileService } from 'shared/services/profileService';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import SkillsData from '../../data/Skills.json';
import { ActiveTabContainer, MainCont, TabContainer, TabHeader, TabItem } from './Media.styles';
import useImagesHelper from './useImagesHelper';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { FileType } from 'shared/enums/FileType';

const Dashboard = React.lazy(() => import('./Dashboard/Dashboard'));
const Images = React.lazy(() => import('./Images/Images'));
const Videos = React.lazy(() => import('./Videos/Videos'));
const Audios = React.lazy(() => import('./Audios/Audios'));
const Documents = React.lazy(() => import('./Documents/Documents'));

const { updateTalent, getSkills } = talentService();
const { getMediaList, deleteMedia: postDelete } = mediaService();
const { patchMultipleMedia, patchMediaTags, uploadMedia } = uploadService();
const { setSelectProfileMedia, getProfilesGraphQL } = profileService();

const Media: React.FC = () => {
  const theme = useTheme();
  const { mutateAsync: updateSkillAsync } = updateTalent();
  const { data: skillsData } = getSkills();
  const { data: mediaData, isLoading: isMediaLoading, refetch: refetchMediaList } = getMediaList({ media_bin: true });
  const { mutateAsync: patchMultipleMediaAsync } = patchMultipleMedia();
  const { mutateAsync: patchMediaTagsAsync } = patchMediaTags();
  const { mutateAsync: uploadMediaMutateAsync } = uploadMedia();
  const {
    primaryId,
    showEdit,
    files,
    setShowEdit,
    setFetchingImageUrlToFile,
    mediaTags,
    fetchingTags,
    openEditor,
    onEdit,
  } = useImagesHelper();
  const { mutateAsync, isLoading } = postDelete();
  const { tab } = useParams() as { tab: string };
  const history = useHistory();
  const { mutateAsync: bindAsync } = setSelectProfileMedia();

  const [images, setimages] = useState<IMedia[]>([]);
  const [videos, setvideos] = useState<IMedia[]>([]);
  const [audios, setaudios] = useState<IMedia[]>([]);
  const [documents, setdocuments] = useState<IMedia[]>([]);
  const queryClient = useQueryClient();

  const { data } = getProfilesGraphQL('');
  const profiles = useMemo(() => {
    return data?.data.profiles;
  }, [data]);

  const checkImagePermission = (profile: IProfileGraphQL) => {
    const use_global_permission =
      // If use own permission is false
      !profile?.use_own_talent_permissions ||
      // if the individual talent permission is null or undefined
      profile?.talent_permissions === null ||
      profile?.talent_permissions === undefined;

    // Permission
    const permissions: string[] =
      (use_global_permission ? profile?.agency?.talent_permissions : profile?.talent_permissions) || [];

    return permissions.includes('Images');
  };

  const agencies = useMemo(() => {
    return (
      profiles?.map((profile) => ({
        current: false,
        division: profile.division?.name || '',
        id: profile.id,
        name: profile.agency?.full_name || 'Non-Represented',
        disabled: profile.agency?.full_name && !checkImagePermission(profile),
      })) || []
    );
  }, [profiles]);

  const [selectedAgencies, setSelectedAgencies] = useState(agencies?.filter((agency) => !agency.disabled) || []);

  useEffect(() => {
    if (mediaData) {
      const imgs = mediaData.data.filter((f) => f.attributes.file_type === 'image');
      const vids = mediaData.data.filter((f) => f.attributes.file_type === 'video');
      const auds = mediaData.data.filter((f) => f.attributes.file_type === 'audio');
      const docs = mediaData.data.filter((f) => f.attributes.file_type === 'document');
      setimages([...imgs]);
      setvideos(vids);
      setaudios(auds);
      setdocuments(docs);
    }
  }, [mediaData, isMediaLoading]);

  useEffect(() => {
    const start = async () => {
      if (skillsData?.data.attributes.skills === null) {
        await updateSkillAsync({
          skills: SkillsData as ISkillCategory[],
        });
        await queryClient.invalidateQueries(['talents/skills']);
      }
    };
    start();
  }, [skillsData, updateSkillAsync, queryClient]);

  const onProgress: IOnProgress = (progress: number) => {
    // console.log({ progress });
  };

  const deleteMedia = async (mediaIds: string[], fileType?: FileType) => {
    const resp = await mutateAsync({ mediumIds: mediaIds, onProgress });
    if (resp) {
      queryClient.invalidateQueries(['media']);
      if (fileType) {
        const queries = queryClient
          .getQueryCache()
          .getAll()
          .filter((query) => {
            const key = query.queryKey;

            return (
              Array.isArray(key) &&
              key.length === 3 && // Assuming the key has exactly three elements
              key[0] === REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media &&
              key[2] === fileType
            );
          });

        queries.map((queryKey) =>
          queryClient.invalidateQueries(queryKey.queryKey, {
            refetchInactive: true,
          }),
        );
      }
    }
  };

  const onDropImages = (images: File[]) => {
    openEditor(images);
  };

  const onClickEditImage = (images: IMedia[]) => {
    onEdit(images);
  };

  const onSelectAllImages = (images: IMedia[]) => {
    onEdit(images);
  };

  const tabs: ITab[] = [
    {
      name: 'dashboard',
      header: 'Dashboard',
      component: (
        <MemoizedErrorBoundary>
          <Suspense fallback={<Backdrop isLoading />}>
            <Dashboard
              images={images}
              videos={videos}
              audios={audios}
              documents={documents}
              refetchMediaList={refetchMediaList}
              onDropImages={onDropImages}
              onClickEditImage={onClickEditImage}
            />
          </Suspense>
        </MemoizedErrorBoundary>
      ),
      disabled: false,
    },
    {
      name: 'images',
      header: `Images (${images.length})`,
      component: (
        <MemoizedErrorBoundary>
          <Suspense fallback={<Backdrop isLoading />}>
            <Images
              data={images}
              deleteMedia={deleteMedia}
              onClickEditImage={onClickEditImage}
              onSelectAllImages={onSelectAllImages}
              onDropImages={onDropImages}
            />
          </Suspense>
        </MemoizedErrorBoundary>
      ),
      disabled: false,
    },
    {
      name: 'videos',
      header: `Videos (${videos.length})`,
      component: (
        <MemoizedErrorBoundary>
          <Suspense fallback={<Backdrop isLoading />}>
            <Videos data={videos} deleteMedia={deleteMedia} />
          </Suspense>
        </MemoizedErrorBoundary>
      ),
      disabled: false,
    },
    {
      name: 'audios',
      header: `Audio (${audios.length})`,
      component: (
        <MemoizedErrorBoundary>
          <Suspense fallback={<Backdrop isLoading />}>
            <Audios data={audios} deleteMedia={deleteMedia} />
          </Suspense>
        </MemoizedErrorBoundary>
      ),
      disabled: false,
    },
    {
      name: 'documents',
      header: `Documents (${documents.length})`,
      component: (
        <MemoizedErrorBoundary>
          <Suspense fallback={<Backdrop isLoading />}>
            <Documents data={documents} deleteMedia={deleteMedia} />
          </Suspense>
        </MemoizedErrorBoundary>
      ),
      disabled: false,
    },
  ];

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push(`${ROUTES.TALENT.MEDIA}/${newValue}`);
  };

  const getActiveTab = () => {
    return tabs.find((_tab) => _tab.name === tab)?.component;
  };

  const bindToProfile = async (payload: IProfileMediaSetSelectPayload, imgID: string, profileID: string) => {
    await bindAsync({
      profileId: profileID,
      payload,
    });
    queryClient.invalidateQueries(['profile_media', profileID, { file_type: 'image' }]);
    queryClient.invalidateQueries(['media', { file_type: 'image' }]);
    queryClient.invalidateQueries(['profile', profileID]);
  };

  useSetHeaderTitle(`Media`);
  useSetHeaderBack(false);
  if (isMediaLoading) return <Backdrop isLoading={true} />;

  const uploadMediaCallback = async (data: any, agencies: any) => {
    try {
      if (data && agencies.length) {
        const { id } = data.data;
        const promises = agencies.map(async (agency: any) => {
          return await bindToProfile(
            {
              id,
              tag_list: 'image',
            },
            id,
            agency.id,
          );
        });
        await Promise.all(promises);
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  return (
    <MainCont>
      <TabContainer>
        <TabHeader
          value={tab || 'images_video_audio'}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <TabItem
              key={index}
              label={tab.header}
              disabled={tab.disabled}
              value={tab.name}
              style={{ textTransform: 'capitalize' }}
            />
          ))}
        </TabHeader>
        <ActiveTabContainer>
          {getActiveTab || (
            <Dashboard
              onClickEditImage={onClickEditImage}
              images={images}
              videos={videos}
              audios={audios}
              documents={documents}
              onDropImages={onDropImages}
            />
          )}
        </ActiveTabContainer>
      </TabContainer>
      {showEdit && files && mediaTags && !fetchingTags && skillsData ? (
        <AsyncPhotoEditorV2
          selectedAgencies={selectedAgencies}
          setSelectedAgencies={setSelectedAgencies}
          agencies={agencies}
          enableUndo={true}
          theme={theme.palette.type}
          variant="default"
          {...{
            patchMultipleMediaAsync,
            patchMediaTagsAsync,
            uploadMediaMutateAsync,
            updateSkillAsync,
            uploadMediaCallback,
          }}
          featureEnableRenderSkillTags={true}
          skillsData={skillsData.data}
          mediaData={images}
          mediaTags={mediaTags?.data || []}
          show={showEdit}
          images={files}
          close={() => {
            document.body.style.overflow = 'auto';
            setTimeout(() => (document.body.style.overflow = 'auto'), 0); // Not applying when no timeouts
            setShowEdit(false);
            setFetchingImageUrlToFile(false);
            queryClient.invalidateQueries(['media']);
          }}
          onSuccessImageUpdate={() => {
            queryClient.invalidateQueries(['media']);
            queryClient.invalidateQueries(['talents/skills']);
          }}
          onSuccessImageUpload={async (data: any) => {
            document.body.style.overflow = 'auto'; // Fix the scroll dissapearing
            queryClient.invalidateQueries(['media']);
            queryClient.invalidateQueries(['talents/skills']);
            const queries = queryClient
              .getQueryCache()
              .getAll()
              .filter((query) => {
                const key = query.queryKey;

                return (
                  Array.isArray(key) &&
                  key.length === 3 && // Assuming the key has exactly three elements
                  key[0] === REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media &&
                  key[2] === FileType.IMAGE
                );
              });

            queries.map((queryKey) =>
              queryClient.invalidateQueries(queryKey.queryKey, {
                refetchInactive: true,
              }),
            );
          }}
        />
      ) : (
        <React.Fragment />
      )}
    </MainCont>
  );
};

export default Media;
